import { CompanySummary } from './CompanySummary';
import { EmployerInsightListItem } from './EmployerInsightsListItem';
import { PremiumHeader } from './PremiumHeader';
import {
    Divider,
    Box,
    Chat,
    Flex,
    Heading,
    RecentSearch,
    Responsive,
    Show,
} from '../../../../../../mads';
import { useI18n } from '../../../../../../shared/I18n';
import { useViewjobOneGraphModel } from '../../../../context/ViewJobDataContext';

import { colors, spacing } from '@indeed/ifl-tokens/react-native';

interface EmployerInsightsProps {
    isActivelyReviewApplications: boolean;
    lastActiveTimeInDays: number;
    percentageResponsiveness: number;
    responseTimeInDays: number;
    hasAccess?: boolean;
    companySummary?: string;
}

export function EmployerInsights(props: EmployerInsightsProps) {
    const i18n = useI18n();

    const responsivenessDisplayText = i18n.ngettext(
        'Often responds within {0} day',
        'Often responds within {0} days',
        props.responseTimeInDays,
        [props.responseTimeInDays],
    );

    const responseRateDisplayText =
        props.percentageResponsiveness === 100
            ? i18n.pgettext('Employer Insights response rate', 'Responds to all applications')
            : i18n.pgettext('Employer Insights response rate', 'Responds to many applications');

    return (
        <Box
            id="employerInsights"
            data-testid="employer-insights"
            sx={[{ marginBlockStart: spacing.s5 }]}
        >
            {props.companySummary && (
                <CompanySummary companySummaryText={props.companySummary} />
            )}
            {props.hasAccess && (
                <>
                    <PremiumHeader />
                    <Heading level={4} sx={{ marginBottom: spacing.s5 }}>
                        {i18n.pgettext('Employer Insights Section title', 'Employer insights')}
                    </Heading>
                    <Flex
                        sx={{
                            flexDirection: 'column',
                            gap: spacing.s4,
                            marginBlockEnd: spacing.s5,
                        }}
                    >
                        <EmployerInsightListItem
                            icon={Responsive}
                            iconColor={colors.expressiveAqua700}
                            headingText={responsivenessDisplayText}
                        />
                        <EmployerInsightListItem
                            icon={Chat}
                            iconColor={colors.neutral1000}
                            headingText={responseRateDisplayText}
                            description={i18n.pgettext(
                                'Employer Insights Note about responsiveness to applications',
                                'Based on activity in the last 30 days',
                            )}
                        />
                        {props.isActivelyReviewApplications && (
                            <EmployerInsightListItem
                                icon={Show}
                                iconColor={colors.neutral1000}
                                headingText={i18n.pgettext(
                                    'Employer Insights Note about employer is actively reviewing applications',
                                    'Actively reviewing applications',
                                )}
                                description={i18n.pgettext(
                                    'Employer Insights Note about employer review activity in applications',
                                    'Employer has viewed at least one application in the last 30 days',
                                )}
                            />
                        )}
                        {props.lastActiveTimeInDays > -1 && (
                            <EmployerInsightListItem
                                icon={RecentSearch}
                                iconColor={colors.neutral1000}
                                headingText={i18n.npgettext(
                                    'Employer Insights Note about employer last active time',
                                    'Active {0} day ago',
                                    'Active {0} days ago',
                                    props.lastActiveTimeInDays,
                                    [props.lastActiveTimeInDays],
                                )}
                                description={i18n.pgettext(
                                    'Employer Insights Note about employer activity on indeed',
                                    'Based on activity on indeed',
                                )}
                            />
                        )}
                    </Flex>
                    <Divider />
                </>
            )}
        </Box>
    );
}

export const EmployerInsightsWithContext = () => {
    const vjModel = useViewjobOneGraphModel();

    const employerInsightsModel = vjModel?.employerInsightsModel;

    if (!employerInsightsModel) {
        return null;
    }
    return (
        <EmployerInsights
            isActivelyReviewApplications={employerInsightsModel.isActivelyReviewApplications}
            lastActiveTimeInDays={employerInsightsModel.lastActiveTimeInDays}
            percentageResponsiveness={employerInsightsModel.percentageResponsiveness}
            responseTimeInDays={employerInsightsModel.responseTimeInDays}
            hasAccess={employerInsightsModel.hasAccess}
            companySummary={employerInsightsModel.companySummary}
        />
    );
};
